import {
  useContractSignatures,
  useReprocessContractPdf,
  type Contract,
} from "@/hooks/useContracts";
import { Check, Loader2, RefreshCw } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Button } from "@/components/ui/button";

interface Props {
  contract: Contract;
}

const when = (iso: string | null | undefined) =>
  iso ? format(new Date(iso), "dd/MM · HH:mm", { locale: ptBR }) : "aguardando";

type Step = { label: string; at: string | null; done: boolean; bad?: boolean };

/**
 * Five-step "Trilha de assinatura" from the prototype:
 * Contrato criado / Enviado ao cliente / Visualizado por X · IP /
 * Assinatura do cliente / Contra-assinatura.
 */
export function ContractSignatureTimeline({ contract }: Props) {
  const { data: sigs = [] } = useContractSignatures(contract.id);
  const reprocess = useReprocessContractPdf();
  const allSigned = sigs.length > 0 && sigs.every((s) => s.status === "signed");
  const stuck = allSigned && !contract.signed_pdf_url;

  // Signer 0 is the client (first in order), any further signer is the
  // counter-signature (admin). Falls back gracefully when only one exists.
  const client = sigs.find((s) => s.order_index === 0) ?? sigs[0] ?? null;
  const counter = sigs.find((s) => s.order_index > 0) ?? null;
  const rejected = sigs.find((s) => s.status === "rejected") ?? null;

  const sentAt = sigs.reduce<string | null>(
    (acc, s) => (s.sent_at && (!acc || s.sent_at < acc) ? s.sent_at : acc),
    null,
  );
  // A pending link past expires_at (30 days) is dead for the client — the
  // only fix is "Reenviar para assinatura", so say it here instead of showing
  // "aguardando" forever.
  const now = Date.now();
  const expired = sigs.filter(
    (s) => s.status === "pending" && s.expires_at && new Date(s.expires_at).getTime() < now,
  );

  const steps: Step[] = [
    { label: "Contrato criado", at: contract.created_at, done: true },
    { label: "Enviado ao cliente", at: sentAt, done: !!sentAt },
    {
      label: client?.viewed_at
        ? `Visualizado por ${client.signer_name.split(" ")[0]}${client.viewed_ip ? ` · IP ${client.viewed_ip}` : ""}`
        : "Visualizado pelo cliente",
      at: client?.viewed_at ?? null,
      done: !!client?.viewed_at,
    },
    rejected && rejected.id === client?.id
      ? { label: "Recusado pelo cliente", at: rejected.rejected_at, done: true, bad: true }
      : {
          label: "Assinatura do cliente",
          at: client?.signed_at ?? null,
          done: client?.status === "signed",
        },
    {
      label: "Contra-assinatura",
      at: counter?.signed_at ?? contract.finalized_at ?? null,
      done: counter ? counter.status === "signed" : !!contract.finalized_at,
    },
  ];

  return (
    <div>
      <div className="mb-3 text-xs font-bold text-muted-foreground">Trilha de assinatura</div>
      {steps.map((s) => (
        <div key={s.label} className="flex items-center gap-3 border-b border-border py-[9px]">
          <span
            className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-ok-foreground ${
              s.bad ? "bg-bad" : s.done ? "bg-ok" : "bg-border"
            }`}
          >
            {s.done && !s.bad ? <Check className="h-3 w-3" /> : s.bad ? "×" : ""}
          </span>
          <span className="flex-1 truncate text-[12.5px] font-semibold">{s.label}</span>
          <span className="text-[11.5px] text-muted-foreground">{when(s.at)}</span>
        </div>
      ))}
      {expired.length > 0 && (
        <p className="mt-3 rounded-lg border border-warn/20 bg-warn-soft p-2.5 text-xs text-warn">
          Link expirado para {expired.map((s) => s.signer_name).join(", ")}. Clique em "Reenviar
          para assinatura" para gerar novos links.
        </p>
      )}
      {rejected?.rejection_reason && (
        <p className="mt-3 rounded-lg border border-bad/20 bg-bad-soft p-2.5 text-xs text-bad">
          Motivo: {rejected.rejection_reason}
        </p>
      )}
      {stuck && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-bad/20 bg-bad-soft p-2.5">
          <p className="text-xs text-bad">
            Todos assinaram, mas a geração do PDF final falhou — o cliente ainda não recebeu o
            documento assinado por e-mail.
          </p>
          <Button
            size="sm"
            variant="outline"
            className="h-7 shrink-0 gap-1 text-xs"
            disabled={reprocess.isPending}
            onClick={() => reprocess.mutate(contract.id)}
          >
            {reprocess.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}
            Reprocessar PDF assinado
          </Button>
        </div>
      )}
    </div>
  );
}
